import { PREDICTON_COLORS, LOCATION_VALUES_FOR_PREDICTION_CHART } from "../../components/components.constants";
import { i18n } from "../../translate/i18n";

const { t } = i18n;

const NO_CONNECTOR_LOCATIONS = [
    LOCATION_VALUES_FOR_PREDICTION_CHART.OPPORTUNITY_ACCORDION,
    LOCATION_VALUES_FOR_PREDICTION_CHART.OPPORTUNITY_ACCORDION_ADVANCED_OPP_BTN
];

const colorMap = {
    clientPrediction: "#BFBFBF",
    aidronPrediction: "#f58b4d",
};

const getProductPrediction = (month, predictionData) => {
    const prediction = predictionData.find(
        prediction => prediction.year === month.year && prediction.month === month.month
    );
    return prediction ?? {};
};

const defineFutureLines = (enableAdvancedLines) => {
    if(enableAdvancedLines) {
        return [
        {
            label: t("currentPlan"),
            name: "clientPrediction",
            lineStyle: "shortDash",
        },
        {
            label: t("aiForecast"),
            name: "aidronPrediction",
            lineStyle: "shortDash",
        },
        ];
    }

    return [
        {
            label: t("currentPlan"),
            name: "clientPrediction",
            lineStyle: "shortDash",
        },
    ];
};

const convertMonthStrToNumber = (mesStr) => {
    const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
    return months.indexOf(mesStr.toLowerCase()) + 1;
};

// estratégias
const strategyAccuracyChart = (productPredictionsData, productId) => {
  const futureKeys = new Set(
    productPredictionsData?.latestForecastPredictions[productId]?.map(m => `${m.month}-${m.year}`)
  );

  const additionalHistoricMonths = productPredictionsData?.historicMonthsToDisplay.filter(historic => {
    const key = `${historic.month}-${historic.year}`;
    const hasPrediction = productPredictionsData?.predictionsToUse
      ? productPredictionsData.predictionsToUse.some(pred => pred.month === historic.month && pred.year === historic.year)
      : false;

    return !futureKeys.has(key) && hasPrediction;
  });

  const combinedPredictions = additionalHistoricMonths
    ?.map(({ month, year }) =>
      productPredictionsData?.predictionsToUse.find(pred => pred.month === month && pred.year === year) ?? null
    )
    .filter(Boolean);

  return combinedPredictions?.filter(prediction => prediction.officialClientPrediction !== null) || [];
};

const strategyAccuracyChartDefineFutureMonthsData = (productId, productPredictionsData, latestForecast, productPredictionsFiltered) => {

    const definePredictionToUse = (columnLabel, month, latestForecast) => {
        const hoje = new Date();
        const isCurrentMonth = month.month === (hoje.getMonth() + 1) && month.year === hoje.getFullYear();

        const isFutureMonth =
            month?.year > latestForecast?.year ||
            (month?.year <= latestForecast?.year && month.month > latestForecast?.month);

        switch (columnLabel) {
            // case t("aiForecast"): {
            //     const fieldName = isCurrentMonth && month?.officialAidronPrediction != null
            //         ? "officialAidronPrediction"
            //         : "aidronPrediction";
            //     return fieldName;
            // }
            case t("aiForecast"): 
                return "aidronPrediction";
            
            case t("currentPlan"):
                if (isCurrentMonth || isFutureMonth) return "clientPrediction";
                return "officialClientPrediction";

            default:
                return "invalid";
        }
    };

    const futureColumnLabels = [
        {
            label: t("aiForecast"),
            name: definePredictionToUse,
            color: PREDICTON_COLORS[0],
            lineStyle: "shortDash",
        },
        {
            label: t("currentPlan"),
            name: definePredictionToUse,
            color: PREDICTON_COLORS[1],
            lineStyle: "shortDash",
        },
    ];

    // Meses em que o Forecast IA tem previsão válida (aidronPrediction != null).
    // Usado para limitar o "Plano Atual" aos mesmos meses do Forecast IA, mesmo que
    // o Plano Atual tenha dado extra vindo do merge de historicMonths (strategyAccuracyChart).
    // PARA REVERTER: remover esse bloco e remover o "if (columnLabel.label === t("currentPlan")) {...}"
    // adicionado dentro do monthsToMap.map abaixo — isso volta a deixar o Plano Atual exibir
    // meses que o Forecast IA não tem (ex: Jun/26 vindo de officialClientPrediction).
    const forecastIAMonthKeys = new Set(
        productPredictionsData?.latestForecastPredictions[productId]
            ?.filter(prediction => prediction.aidronPrediction != null)
            .map(prediction => `${prediction.year}-${prediction.month}`)
    );

    const result = [];

    futureColumnLabels.forEach((columnLabel, i) => {
        const predictionsByYearMonth = {};
        
        productPredictionsData?.latestForecastPredictions[productId]?.forEach(prediction => {
            const key = `${prediction.year}-${prediction.month}`;
            predictionsByYearMonth[key] = prediction;
        });

        if (columnLabel.label === t("currentPlan")) {
            const addictionalHistoricMonths = strategyAccuracyChart(productPredictionsData, productId);
            addictionalHistoricMonths.forEach(prediction => {
                const key = `${prediction.year}-${prediction.month}`;
                if (!predictionsByYearMonth[key]) predictionsByYearMonth[key] = prediction;
            });
        }

        const monthsToMap = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    
        
        let values = monthsToMap.map(monthNumber => {
            const yearsPresent = [...new Set(
                Object.values(predictionsByYearMonth)
                    .map(entry => entry.year)
                    .filter(year => year !== undefined)
            )].sort((a, b) => a - b);

            let prediction = predictionsByYearMonth[`${yearsPresent[1]}-${monthNumber}`];

            if (!prediction) prediction = predictionsByYearMonth[`${yearsPresent[0]}-${monthNumber}`];
            if (!prediction) return null;

            // Restringe o Plano Atual aos meses que o Forecast IA também possui.
            // PARA REVERTER: remover este bloco "if".
            if (columnLabel.label === t("currentPlan")) {
                const key = `${prediction.year}-${prediction.month}`;
                if (!forecastIAMonthKeys.has(key)) return null;
            }

            const columnLabelName = columnLabel.name(columnLabel.label, prediction, latestForecast);

            const value = prediction[columnLabelName];

            return value ?? null;
        });

        const hasValidData = values.some(value => value !== null && value !== undefined);
        if (!hasValidData) return;

        const segments = [];
        let currentSegment = [];
        let currentStartIndex = -1;

        for (let i = 0; i < values.length; i++) {
            if (values[i] !== null) {
            if (currentSegment.length === 0) {
                currentStartIndex = i;
            }
            currentSegment.push({ index: i, value: values[i] });
            } else {
            if (currentSegment.length > 0) {
                segments.push({ startIndex: currentStartIndex, data: currentSegment });
                currentSegment = [];
            }
            }
        }
    
        if (currentSegment.length > 0) segments.push({ startIndex: currentStartIndex, data: currentSegment });
        if (segments.length === 0) return;

        segments.forEach((segment, segmentIndex) => {
            const segmentValues = Array(12).fill(null);
            
            segment.data.forEach(({ index, value }) => {
            segmentValues[index] = value;
            });

            const DISABLE_FIRST_SEGMENT_CONNECTION = segments.length === 1;

            if (segmentIndex === 0 && segment.startIndex > 0 && !DISABLE_FIRST_SEGMENT_CONNECTION) {
            const lastRealizedValue = productPredictionsData?.monthSequences.allHistoricMonths && productPredictionsData?.monthSequences.allHistoricMonths.length > 0 
                ? (() => {
                    const lastMonth = productPredictionsData?.monthSequences.allHistoricMonths[productPredictionsData?.monthSequences.allHistoricMonths.length - 1];
                    const lastPrediction = getProductPrediction(lastMonth, productPredictionsFiltered);
                    return lastPrediction?.realized || 0;
                })()
                : 0;

            segmentValues[segment.startIndex - 1] = {
                y: lastRealizedValue,
                marker: { enabled: false, hover: { enabled: false } },
            };
            }

            let customLabel = columnLabel.label;
            if ((columnLabel.label === t("aiForecast") || columnLabel.label === t("currentPlan")) && segments.length > 1) {
                const firstEntry = segment.data.find(({ value }) => value !== null);
                const year = firstEntry ? Object.values(predictionsByYearMonth)
                    .find(p => p[columnLabel.name(columnLabel.label, p, latestForecast)] === firstEntry.value)?.year : null;

                customLabel = `${columnLabel.label} ${year ?? ''}`.replace(/\s*\(\d+\)$/, '');
                } else if (segmentIndex > 0) {
                customLabel = `${columnLabel.label} (${segmentIndex + 1})`;
                customLabel = customLabel.replace(/\s*\(\d+\)$/, '');
            }       

            const segmentColor = (() => {
                if (customLabel.includes(t("aiForecast"))) return PREDICTON_COLORS[0];
                if (customLabel.includes(t("currentPlan"))) {
                    if (segments.length === 1) return PREDICTON_COLORS[1];
                    return PREDICTON_COLORS[1]
                }
                return columnLabel.color;
            })();        

            const validValuesCount = segmentValues.filter(v => v !== null && v !== undefined).length;
            result.push({
            ...columnLabel,
            label: customLabel,
            values: segmentValues,
            marker: validValuesCount <= 2,
            color: segmentColor
            });
        });
    });

    return result;
};

const predictionChartDefineFutureMonthsData = (
    lastRealizedValue = [], 
    productPredictionsData = [], 
    enableAdvancedLines, 
    clientPlanValuesFromPastMonths, 
    location = "default"
) => {

    const defineAdditionalHistoricMonths = (productPredictionsData, withPredictions = false) => {
        if (withPredictions) {
            const futureKeys = productPredictionsData?.monthColumns
            ? new Set(productPredictionsData?.monthColumns.map(m => `${m.month}-${m.year}`))
            : new Set();

            const additionalHistoricMonths = productPredictionsData?.historicMonthsToDisplay?.filter(historic => {
            const key = `${historic.month}-${historic.year}`;
            const hasPrediction = productPredictionsData?.predictionsToUse
                ? productPredictionsData?.predictionsToUse.some(pred => pred.month === historic.month && pred.year === historic.year)
                : false;

            return !futureKeys.has(key) && hasPrediction;
            });

            const combinedPredictions = additionalHistoricMonths
            ?.map(({ month, year }) =>
                productPredictionsData?.predictionsToUse.find(pred => pred.month === month && pred.year === year) ?? null
            )
            .filter(Boolean);

            return combinedPredictions;
        }

        const futureKeys = new Set(productPredictionsData?.monthColumns.map(m => `${m.month}-${m.year}`));

        const additionalHistoricMonths = productPredictionsData?.historicMonthsToDisplay.filter(historic => {
            const key = `${historic.month}-${historic.year}`;
            const hasPrediction = productPredictionsData?.predictionsToUse.some(
            pred => pred.month === historic.month && pred.year === historic.year
            );
            return !futureKeys.has(key) && hasPrediction;
        });

        return additionalHistoricMonths;
    };    

    const columnLabels = defineFutureLines(enableAdvancedLines) || [];

    if(clientPlanValuesFromPastMonths) {
        const allMonthsWithPredictions = defineAdditionalHistoricMonths(productPredictionsData, true) || [];
        const monthsToUSe = allMonthsWithPredictions.concat(productPredictionsData?.monthColumns) || [];
        
        const result = columnLabels?.map((columnLabel, i) => {
            let values = Array.isArray(productPredictionsData?.xLabels)
                ? productPredictionsData.xLabels.map(label => {
                    const [mesStr, anoStr] = label.split("/");
                    const month = convertMonthStrToNumber(mesStr);
                    const year = parseInt(`20${anoStr}`);

                    const matched = monthsToUSe.find(m => m.month === month && m.year === year);
                    const rawValue = matched?.[columnLabel.name];

                    return rawValue != null ? rawValue : null;
                })
                : []

        const firstValidIndex = values?.findIndex(v => v !== null) || [];

        const paddedValues = values?.map((v, idx) => {
            if (idx < firstValidIndex) return null;
            return v;
        });

        if (firstValidIndex > 0 && !NO_CONNECTOR_LOCATIONS.includes(location)) {
            paddedValues[firstValidIndex - 1] = {
                y: lastRealizedValue,
                marker: {
                    enabled: false,
                    states: { hover: { enabled: false } }
                },
            };
        }

        values = paddedValues;      

        const item = {
            label: columnLabel.label,
            color: colorMap[columnLabel.name],
            lineStyle: columnLabel.lineStyle,
            values: values,
            marker: true,
        };

        return item;
        });

        return result;
    }

    const result = (columnLabels || []).map((columnLabel, i) => {
        let values = Array.isArray(productPredictionsData?.xLabels)
            ? productPredictionsData.xLabels.map(label => {
                const [mesStr, anoStr] = label.split("/");
                const month = convertMonthStrToNumber(mesStr);
                const year = parseInt(`20${anoStr}`);

                const matched = productPredictionsData?.monthColumns
                    ? productPredictionsData.monthColumns.find(
                            m => m.month === month && m.year === year
                        )
                    : null;

                const rawValue = matched?.[columnLabel.name];

                return rawValue != null ? rawValue : null;
            })
        : [];

        const firstValidIndex = values.findIndex(v => v !== null);

        const paddedValues = values.map((v, idx) => {
            if (idx < firstValidIndex) return null;
            return v;
        });

        if (firstValidIndex > 0 && !NO_CONNECTOR_LOCATIONS.includes(location)) {
            paddedValues[firstValidIndex - 1] = {
                y: lastRealizedValue,
                marker: {
                    enabled: false,
                    states: { hover: { enabled: false } }
                },
            };
        }

        values = paddedValues;     

        const item = {
            label: columnLabel.label,
            color: colorMap[columnLabel.name],
            lineStyle: columnLabel.lineStyle,
            values: values,
            marker: true,
        };

        return item;
    });

    return result;
};

const strategyAccuracyChartDefineLines = (pastMonths, productPredictionsFiltered) => {
    const realizedSeries = pastMonths.map(month => {
        const productPrediction = getProductPrediction(month, productPredictionsFiltered);
        const prediction = !isNaN(productPrediction?.realized) ? productPrediction.realized : null;
        return { ...month, realized: prediction };
        });
    return realizedSeries;
};

const strategyPredictionChartDefineLines = (
    productPredictionsData, 
    enableAdvancedLines, 
    clientPlanValuesFromPastMonths, 
    shoulShowRealized = false,
    location = "default"
) => {

    const realized = {
        label: 'Realizado',
        color: "#00BECB",
        values: productPredictionsData?.realized,
        marker: true,
    };

    const futureMonthsData = predictionChartDefineFutureMonthsData(
        productPredictionsData?.lastRealizedValue || 0,
        productPredictionsData,
        enableAdvancedLines, 
        clientPlanValuesFromPastMonths, 
        location
    );

    const showRealized = shoulShowRealized && location === LOCATION_VALUES_FOR_PREDICTION_CHART.OPPORTUNITY_ACCORDION;

    return showRealized ? [realized].concat(futureMonthsData) : futureMonthsData;
};

export const defineFutureMonthsData = {
    accuracyChart: strategyAccuracyChartDefineFutureMonthsData
};

export const futureLinesDefition = {
    accuracyChart: strategyAccuracyChartDefineLines,
    predictionChart: strategyPredictionChartDefineLines
};